<template lang="pug">
  div(class="eds-iit-saved-keys")
    div(class="text-14px dark mb-3") {{ $t('Saved keys') }}
      |
      |
      span(class="text-14px light") ({{ $t('available only in current browser') }})
    div(
      v-for="(item, idx) in formattedKeys"
      :key="item.id"
      class="eds-iit-saved-keys__item"
    )
      button(
        class="eds-iit-saved-keys__item-btn"
        :class="{ selected: selectedItemId === item.id }"
        @click="selectedItemId = item.id"
      )
        div(class="eds-iit-saved-keys__item-content")
          div(class="eds-iit-saved-keys__item-left")
            div(class="text-14px dark pb-2") {{ item.keyName }}
            div(class="text-12px light") {{ $t('File key') }}: {{ item.fileName }}
            div(class="text-12px light") {{ $t('Valid until') }} {{ item.formattedEndTime }}
          div(class="eds-iit-saved-keys__item-right")
            v-btn(
              small
              icon
              class="mb-2"
              @click.stop="deleteKey(item.id)"
            )
              e-svg-icon(size="sm") trash-dark
            e-chip-status(:status="setCurrentKeyStatus(item.endTime)")
      ValidationObserver(
        ref="password"
        slim
      )
        transition(name="fade")
          ValidationProvider(
            v-show="selectedItemId === item.id"
            rules="required"
            vid="keyPassword"
            :name="wrapQuotesProviderName($t('Enter password key'))"
            v-slot="{ errors }"
            mode="passive"
          )
            e-input-password(
              v-model="password"
              :label="$t('Enter password key')"
              type="password"
              :error-messages="errors"
              hide-details="auto"
              filled
            )
    div(class="two-buttons-wrapper pt-8")
      v-btn(
        height="48"
        @click="$emit('goToFileSelect')"
      ) {{ $t('Select another key') }}
      v-btn(
        class="main-button"
        :loading="loading"
        @click="handleSign"
      ) {{ $t('Sign') }}
</template>

<script>
import Base64js from 'base64-js'
import { v4 as uuidv4 } from 'uuid'
import dateTime from '~/mixins/datetime/dateTime'
import { formats } from '~/const/global'
import EChipStatus from '~/components/elements/chips/e-chip-status'
import EInputPassword from '~/components/elements/inputs/e-input-password'
import validate from '~/mixins/validation/validate'
import cryptoEdsKeys from '~/modules/eds-iit-checkbox/mixins/cryptoEdsKeys'
import confirmationDialog from '~/mixins/dialogs/confirmationDialog'
import informationSnackbar from '~/modules/snackbar/mixins/informationSnackbar'

export default {
  name: 'EdsIitSavedKeys',
  components: { EChipStatus, EInputPassword },
  mixins: [dateTime, validate, cryptoEdsKeys, confirmationDialog, informationSnackbar],
  props: {
    endUser: {
      type: Object,
      default: () => ({})
    },
    modelInstance: {
      type: Object,
      default: null
    },
    onInterfaceActionError: {
      type: Function,
      default: () => {}
    },
    onConfirm: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      selectedItemId: null,
      password: null,
      loading: false,
      localKeyData: null,
      keysData: []
    }
  },
  computed: {
    formattedKeys() {
      return this._.map(this.keysData, item => ({
        ...item,
        formattedEndTime: this.formatDate(item.endTime, formats.date)
      }))
    },
    statuses() {
      return {
        active: {
          text: this.$t('Active'),
          color: '#049541',
          backgroundColor: '#F7FCF9'
        },
        unactive: {
          text: this.$t('Unactive'),
          color: '#FF1744',
          backgroundColor: '#FFF8F9'
        },
        ends_soon: {
          text: this.$t('Ends soon'),
          color: '#FFA04C',
          backgroundColor: '#FFFCFA'
        }
      }
    },
    selectedItem() {
      return this._.find(this.keysData, item => item.id === this.selectedItemId)
    },
    savedKeysLS() {
      return this._.get(this.$localStorageClientData, 'savedEdsKeys', [])
    }
  },
  created() {
    if (this.savedKeysLS.length && !this.keysData.length && this.$User) {
      this.getKeysInfo()
    }
  },
  methods: {
    getKeysInfo() {
      this.loading = true
      for (const keyData of this.savedKeysLS) {
        const decryptedData = this.decryptKeyInfo(keyData)
        this.keysData.push({ id: uuidv4(), ...decryptedData })
      }
      this.loading = false
    },
    deleteKey(id) {
      const selectedItem = this._.find(this.keysData, item => item.id === id)
      this.confirmationDialog.open({
        title: this.$t('Delete key'),
        text: this.$t('Do you really want to delete key {keyName}? This action cannot be undone', {
          keyName: selectedItem.keyName
        }),
        buttons: {
          approve: {
            text: this.$t('Delete'),
            class: 'secondary-button danger-btn'
          },
          dismiss: {
            text: this.$t('Cancel')
          }
        },
        icon: {
          src: require('~/assets/images/warning.png'),
          maxWidth: 80
        },
        onConfirm: () => {
          const savedKeysLS = this._.get(this.$localStorageClientData, 'savedEdsKeys', false)
          if (selectedItem) {
            this.$setLocalStorageClientData({
              savedEdsKeys: this._.filter(savedKeysLS, keyData => {
                const decryptedKeyData = this.decryptKeyInfo(keyData)
                return decryptedKeyData.keyBase64 !== selectedItem.keyBase64
              })
            })
            this.keysData = this._.filter(this.keysData, item => item.id !== selectedItem.id)
            if (!this.keysData.length) {
              this.$emit('goToFileSelect')
              this.$emit('hideSavedKeysBtn')
            }
          }
        }
      })
    },
    async handleSign() {
      if (this._.isNull(this.selectedItemId)) {
        await this.informationSnackbar.open({
          text: this.$t('Select key for document signature'),
          textCenter: true
        })
        return
      }
      try {
        // validation part
        this.loading = true
        const valid = await this.$refs?.password[0]?.validate()
        if (!valid) {
          return
        }
        if (!this.endUser) {
          return
        }
        // reading key part
        const fileData = Base64js.toByteArray(this.selectedItem.keyBase64)
        const fileUInt8View = new Uint8Array(fileData)
        if (this.selectedItem.fileName.endsWith('.jks')) {
          const jksKeys = await this.endUser.GetJKSPrivateKeys(fileUInt8View)
          const pkIndex = 0
          const pkCertificates = []
          for (let i = 0; i < jksKeys[pkIndex].certificates.length; i++) {
            pkCertificates.push(jksKeys[pkIndex].certificates[i].data)
          }
          await this.endUser.ReadPrivateKeyBinary(
            jksKeys[pkIndex].privateKey,
            this.password,
            pkCertificates,
            null
          )
        } else {
          await this.endUser.ReadPrivateKeyBinary(fileUInt8View, this.password, null, null)
        }
        this.localKeyData = await this.endUser.GetOwnCertificates()
        // confirm part
        const si = this.modelInstance.getInterface()
        if (!si?.confirmed) {
          this.modelInstance.saveData({
            endUser: this.endUser,
            keyData: this.localKeyData,
            isSeal: !this._.get(this.localKeyData, '[0]infoEx.subjDRFOCode', ''),
            onInterfaceActionError: this.onInterfaceActionError,
            isSmartId: false,
            isDiia: false,
            isCloudProvider: false,
            isStamp: false, // todo: add stamp support
            confirmed: true
          })
        }
        const signInterface = this.modelInstance.getInterface()
        let onConfirmData
        if (this._.isFunction(this.onConfirm)) {
          try {
            onConfirmData = await this.onConfirm(signInterface)
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e)
          }
        }
        if (signInterface?.confirmed) {
          this.$emit('confirm', { ...signInterface, onConfirmData })
        }
        // close modal
      } catch (e) {
        this.$handlers.error(e, this)
        this.$emit('resetLocalData')
      } finally {
        this.loading = false
        this.$emit('resetModelData')
      }
    },
    setCurrentKeyStatus(date) {
      const keyEndDate = this.$moment(date)
      const twoWeeksAgo = this.$moment().subtract(14, 'days')

      if (keyEndDate.isBefore(twoWeeksAgo)) {
        return this.statuses.ends_soon
      } else if (this.$moment().isAfter(keyEndDate)) {
        return this.statuses.unactive
      }
      return this.statuses.active
    }
  }
}
</script>

<style scoped lang="scss">
.eds-iit-saved-keys {
  &__item {
    width: 100%;

    &:not(:last-child) {
      margin-bottom: 12px;
    }

    &-btn {
      width: 100%;
      height: 100%;
      padding: 12px 16px;
      margin-bottom: 12px;
      border-radius: 12px;
      border: 1px solid #e3e3e3;
      background: $white;

      &.selected {
        border: 1px solid $primary;
        background: $primary-light;
      }
    }

    &-content {
      display: flex;
      justify-content: space-between;
    }

    &-left {
      text-align: left;
    }

    &-right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: space-between;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
